import { useEffect, useState, useRef } from "react";
import { Send, ShieldCheck, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function Messages() {
    const { user } = useAuth();
    const [threads, setThreads] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const endRef = useRef(null);

    useEffect(() => {
        api.get("/threads").then(({ data }) => {
            setThreads(data.items || []);
            if (data.items?.length) setActive(data.items[0]);
        });
    }, []);

    useEffect(() => {
        if (!active) return;
        api.get(`/threads/${active.id}/messages`).then(({ data }) => {
            setMessages(data.items || []);
        });
    }, [active]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || !active) return;
        try {
            const { data } = await api.post("/messages", { thread_id: active.id, content: text });
            setMessages([...messages, data.message]);
            setText("");
        } catch (e) {}
    };

    return (
        <div data-testid="messages-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="font-display text-3xl font-bold mb-2">Secure Messages</h1>
            <p className="font-app text-sm text-muted-foreground mb-6 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-fiji-green" /> All messages are routed through Bula Real Estate Fiji.
            </p>

            <Card className="overflow-hidden">
                <div className="grid md:grid-cols-3 min-h-[60vh]">
                    {/* Threads list */}
                    <div className="border-r border-border bg-secondary/30">
                        <div className="p-4 border-b border-border">
                            <h2 className="font-display font-semibold">Conversations</h2>
                        </div>
                        <div className="divide-y divide-border">
                            {threads.length === 0 && (
                                <div className="p-6 text-center text-sm text-muted-foreground font-app">
                                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
                                    No conversations yet.
                                </div>
                            )}
                            {threads.map((t) => (
                                <button
                                    key={t.id}
                                    data-testid={`thread-${t.id}`}
                                    onClick={() => setActive(t)}
                                    className={`w-full text-left p-4 hover:bg-background transition ${active?.id === t.id ? "bg-background" : ""}`}
                                >
                                    <div className="font-app font-medium text-sm truncate">{t.target_title}</div>
                                    <div className="text-xs text-muted-foreground font-app capitalize">{t.target_type} • {t.participants?.length} participants</div>
                                    {t.last_preview && <div className="text-xs text-muted-foreground font-app mt-1 truncate">{t.last_preview}</div>}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="md:col-span-2 flex flex-col">
                        {!active ? (
                            <div className="flex-1 flex items-center justify-center text-muted-foreground font-app">Select a conversation</div>
                        ) : (
                            <>
                                <div className="p-4 border-b border-border">
                                    <div className="font-display font-semibold">{active.target_title}</div>
                                    <div className="text-xs text-muted-foreground font-app">Routed via platform · Read-only for admins</div>
                                </div>
                                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
                                    {messages.map((m) => {
                                        const mine = m.from_user_id === user?.id;
                                        return (
                                            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-ocean text-white" : "bg-secondary text-foreground"}`}>
                                                    {!mine && <div className="text-[10px] uppercase tracking-wider font-app font-semibold opacity-70 mb-1">{m.from_user_name}</div>}
                                                    <div className="text-sm font-app whitespace-pre-wrap">{m.content}</div>
                                                    <div className={`text-[10px] mt-1 font-app ${mine ? "text-white/60" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleString()}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={endRef} />
                                </div>
                                <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
                                    <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your message…" />
                                    <Button data-testid="send-message-btn" type="submit" className="bg-coral text-white hover:bg-coral-deep">
                                        <Send className="h-4 w-4" />
                                    </Button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
}
